import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { IndianRupee, Plus, ReceiptText } from "lucide-react";

const paymentModes = ["CASH", "UPI", "BANK_TRANSFER", "CHEQUE", "OTHER"];

const formatAmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatKg = (v) => Number(v || 0).toFixed(3);

const Accounts = () => {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [summary, setSummary] = useState({ rows: [], totals: {} });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    party_id: "",
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "",
    mode: "CASH",
    reference_no: "",
    remarks: "",
  });

  const monthInt = Number(month);
  const yearInt = Number(year);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, paymentRes] = await Promise.all([
        api.accounts.getMonthlySummary({ month: monthInt, year: yearInt }),
        api.accounts.listPayments({ month: monthInt, year: yearInt, limit: 200 }),
      ]);
      setSummary(summaryRes.data || { rows: [], totals: {} });
      setPayments(paymentRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load accounts data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!monthInt || !yearInt) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthInt, yearInt]);

  const partyOptions = useMemo(() => {
    return (summary.rows || []).map((r) => ({ id: r.party_id, name: r.party_name }));
  }, [summary.rows]);

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.party_id) return toast.error("Please select a party");
    const amt = Number(paymentForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Amount must be greater than 0");

    setSubmittingPayment(true);
    try {
      await api.accounts.createPayment({
        party_id: paymentForm.party_id,
        payment_date: paymentForm.payment_date ? new Date(paymentForm.payment_date).toISOString() : new Date().toISOString(),
        amount: amt,
        mode: paymentForm.mode,
        reference_no: paymentForm.reference_no || null,
        remarks: paymentForm.remarks || null,
      });
      toast.success("Payment recorded");
      setOpenPaymentDialog(false);
      setPaymentForm({
        party_id: "",
        payment_date: new Date().toISOString().slice(0, 10),
        amount: "",
        mode: "CASH",
        reference_no: "",
        remarks: "",
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getPaymentStatusLed = (status) => {
    if (status === "PAID") {
      return (
        <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-green bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
          <span className="led-indicator bg-led-green" /> PAID
        </div>
      );
    }
    if (status === "PARTIAL") {
      return (
        <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-amber bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
          <span className="led-indicator bg-led-amber" /> PARTIAL
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-red bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
        <span className="led-indicator bg-led-red" /> UNPAID
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="accounts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-industrial-border pb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Accounts</h1>
          <p className="text-slate-400 mt-2 font-mono text-xs uppercase">Monthly billing, pending stock visibility, and ledger payment status.</p>
        </div>
        
        <Dialog open={openPaymentDialog} onOpenChange={setOpenPaymentDialog}>
          <DialogTrigger asChild>
            <button 
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
              data-testid="add-payment-btn"
            >
              <Plus className="h-4 w-4" /> RECORD PAYMENT
            </button>
          </DialogTrigger>
          
          <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-lg p-6">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Record Party Payment</DialogTitle>
            </DialogHeader>
            
            <form className="space-y-4 text-xs mt-2" onSubmit={handleCreatePayment}>
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Party *</label>
                <select
                  value={paymentForm.party_id}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, party_id: e.target.value }))}
                  className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                  required
                >
                  <option value="">Select party</option>
                  {partyOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Payment Date *</label>
                  <input 
                    type="date" 
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={paymentForm.payment_date} 
                    onChange={(e) => setPaymentForm((p) => ({ ...p, payment_date: e.target.value }))} 
                    required
                  />
                </div>
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Amount *</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={paymentForm.amount} 
                    onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} 
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Mode</label>
                  <select
                    value={paymentForm.mode}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, mode: e.target.value }))}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                  >
                    {paymentModes.map((mode) => (
                      <option key={mode} value={mode}>{mode.replace("_", " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Reference</label>
                  <input 
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={paymentForm.reference_no} 
                    onChange={(e) => setPaymentForm((p) => ({ ...p, reference_no: e.target.value }))} 
                  />
                </div>
              </div>
              
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Remarks</label>
                <input 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={paymentForm.remarks} 
                  onChange={(e) => setPaymentForm((p) => ({ ...p, remarks: e.target.value }))} 
                />
              </div>
              
              <div className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
                <button 
                  type="button" 
                  onClick={() => setOpenPaymentDialog(false)}
                  className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                >
                  CANCEL
                </button>
                <button 
                  type="submit" 
                  disabled={submittingPayment}
                  className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submittingPayment ? "SAVING..." : "SAVE PAYMENT"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters Card */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3">
          <ReceiptText className="h-4 w-4 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Ledger Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1.5 font-bold">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
            >
              {Array.from({ length: 12 }).map((_, idx) => (
                <option key={idx + 1} value={String(idx + 1)}>{idx + 1}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1.5 font-bold">Year</label>
            <input 
              type="number" 
              className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
              value={year} 
              onChange={(e) => setYear(e.target.value)} 
            />
          </div>
          
          <div>
            <button 
              onClick={loadData}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-10 px-6 transition-colors w-full md:w-auto"
            >
              REFRESH
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none">
          <p className="text-xs uppercase font-sans tracking-wider text-slate-400 mb-2 font-semibold">Total Cutting Amount</p>
          <p className="text-2xl font-mono font-bold text-white flex items-center gap-1.5">
            <IndianRupee className="h-5 w-5 text-accent" /> {formatAmt(summary.totals?.total_cutting_amount)}
          </p>
        </div>
        
        <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none">
          <p className="text-xs uppercase font-sans tracking-wider text-slate-400 mb-2 font-semibold">Payment Received</p>
          <p className="text-2xl font-mono font-bold text-white flex items-center gap-1.5">
            <IndianRupee className="h-5 w-5 text-accent" /> {formatAmt(summary.totals?.payment_received_amount)}
          </p>
        </div>
        
        <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none">
          <p className="text-xs uppercase font-sans tracking-wider text-slate-400 mb-2 font-semibold">Balance (Month)</p>
          <p className="text-2xl font-mono font-bold text-white flex items-center gap-1.5">
            <IndianRupee className="h-5 w-5 text-accent" /> {formatAmt(summary.totals?.month_balance_amount)}
          </p>
        </div>
      </div>

      {/* Monthly Summary Table */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="border-b border-industrial-border pb-3">
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Party-wise Monthly Summary</h2>
        </div>
        
        {loading ? (
          <div className="py-8 text-center text-slate-500 font-mono text-xs uppercase">Loading account summary...</div>
        ) : summary.rows?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono text-slate-300">
              <thead>
                <tr className="border-b border-industrial-border text-slate-400 text-[9px] uppercase tracking-wider font-semibold">
                  <th className="text-left py-3 pr-3 font-sans">Party</th>
                  <th className="text-right py-3 px-3">Cutting Amount</th>
                  <th className="text-right py-3 px-3">Payment Received</th>
                  <th className="text-right py-3 px-3">Balance</th>
                  <th className="text-right py-3 px-3">Circle Pending (kg)</th>
                  <th className="text-right py-3 px-3">Scrap Pending (kg)</th>
                  <th className="text-right py-3 px-3">Material Balance Pending (kg)</th>
                  <th className="text-center py-3 pl-3 font-sans">Payment Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-industrial-border/40">
                {summary.rows.map((r, idx) => (
                  <tr key={r.party_id} className={`hover:bg-industrial-s2/20 transition-colors ${idx % 2 === 0 ? "bg-industrial-s2/5" : ""}`}>
                    <td className="py-3 pr-3 text-white font-sans font-bold">{r.party_name}</td>
                    <td className="py-3 px-3 text-right text-white font-bold">{formatAmt(r.total_cutting_amount)}</td>
                    <td className="py-3 px-3 text-right text-white font-bold">{formatAmt(r.payment_received_amount)}</td>
                    <td className="py-3 px-3 text-right text-white font-bold">{formatAmt(r.month_balance_amount)}</td>
                    <td className="py-3 px-3 text-right text-slate-300">{formatKg(r.pending_circle_kg)}</td>
                    <td className="py-3 px-3 text-right text-slate-300">{formatKg(r.pending_scrap_kg)}</td>
                    <td className="py-3 px-3 text-right text-slate-300">{formatKg(r.material_balance_pending_kg)}</td>
                    <td className="py-3 pl-3 text-center">{getPaymentStatusLed(r.payment_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center text-slate-500 font-mono text-xs uppercase">No monthly accounting data found.</div>
        )}
      </div>

      {/* Recent Payments Table */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="border-b border-industrial-border pb-3">
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Recent Payments (Selected Month)</h2>
        </div>
        
        {payments.length === 0 ? (
          <div className="py-4 text-slate-500 font-mono text-xs uppercase">No payments recorded in this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono text-slate-300">
              <thead>
                <tr className="border-b border-industrial-border text-slate-400 text-[9px] uppercase tracking-wider font-semibold">
                  <th className="text-left py-3 pr-3">Date</th>
                  <th className="text-left py-3 px-3 font-sans">Party</th>
                  <th className="text-left py-3 px-3">Mode</th>
                  <th className="text-left py-3 px-3">Reference</th>
                  <th className="text-right py-3 pl-3">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-industrial-border/40">
                {payments.map((p, idx) => (
                  <tr key={p.id} className={`hover:bg-industrial-s2/20 transition-colors ${idx % 2 === 0 ? "bg-industrial-s2/5" : ""}`}>
                    <td className="py-3 pr-3 text-slate-300">{new Date(p.payment_date).toLocaleDateString()}</td>
                    <td className="py-3 px-3 text-white font-sans font-bold">{p.party_name || "-"}</td>
                    <td className="py-3 px-3 text-slate-400 uppercase font-bold text-[10px]">{p.mode}</td>
                    <td className="py-3 px-3 text-slate-300">{p.reference_no || "-"}</td>
                    <td className="py-3 pl-3 text-right text-white font-bold">{formatAmt(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Accounts;
